import React from 'react'
import { useLocation } from 'react-router'
import { parse, stringify } from 'query-string'

import { routes } from '@sketch/modules-common'

import { useIsAnnotationContextAvailable } from 'modules/annotations/hooks'

import AnnotationInboxList from '../../components/AnnotationInboxList'
import ShareSubscriptionStatus from '../../components/ShareSubscriptionStatus'
import CommentTabMessageRestricted from '../../components/CommentTabMessageRestricted'
import { Share } from '../../types'

interface ShareAnnotationsProps {
  share: Share
}

const ShareAnnotations: React.FC<ShareAnnotationsProps> = props => {
  const { share } = props

  const location = useLocation()
  const isAnnotationContextAvailable = useIsAnnotationContextAvailable()

  if (!share.commentsEnabled) {
    return <CommentTabMessageRestricted />
  }

  const queryArguments = parse(location.search)
  const annotation = Array.isArray(queryArguments.annotation)
    ? queryArguments.annotation[0]!
    : queryArguments.annotation || undefined

  return (
    <AnnotationInboxList
      shareIdentifier={share.identifier}
      activeAnnotationIdentifier={annotation}
      navbarAction={
        <ShareSubscriptionStatus shareIdentifier={share.identifier} />
      }
      resolveAnnotationLink={annotation => {
        /*
         * Assume we're dealing with a *floating annotation* unless the user is
         * on the canvas fallback page (i.e. the canvas has failed to load).
         *
         * TODO: disallow floating annotations for CWVs
         * https://github.com/sketch-hq/Cloud/issues/14254
         */
        if (isAnnotationContextAvailable) {
          return location => ({
            ...location,
            state: {
              isFromCommentTab: true,
            },
            search: stringify({
              ...queryArguments,
              annotation: annotation.identifier,
            }),
          })
        }

        /**
         * Otherwise it should default to the artboard
         * view like we have been using.
         *
         * TODO: show non-floating annotations in the document view
         * https://github.com/sketch-hq/Cloud/issues/15430
         */
        if (annotation.currentSubject.__typename === 'Artboard') {
          const permanentArtboardShortId =
            annotation.currentSubject.permanentArtboardShortId

          return routes.ARTBOARD_DETAIL.create({
            shareID: share.identifier,
            permanentArtboardShortId,
            query: { ...queryArguments, annotation: annotation.identifier },
          })
        }
      }}
    />
  )
}

export default ShareAnnotations
